import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Archive, Check, X as XIcon } from "lucide-react";
import { toast } from "sonner";

import {
    api,
    clearLegacySessionId,
    getLegacySessionId,
} from "@/lib/api";
import { useSession } from "@/contexts/SessionContext";

/**
 * Shown once after first login when the user's localStorage holds a
 * pre-auth anonymous session_id AND the backend reports legacy data
 * exists for it. One-time merge offer with Accept / Dismiss.
 */
export default function LegacyClaimBanner() {
    const { isAuthenticated, refreshAll } = useSession();
    const [legacySid, setLegacySid] = useState(null);
    const [counts, setCounts] = useState(null);
    const [busy, setBusy] = useState(false);
    const [dismissed, setDismissed] = useState(false);

    useEffect(() => {
        if (!isAuthenticated || dismissed) return;
        const sid = getLegacySessionId();
        if (!sid) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get(`/auth/legacy-check/${sid}`);
                if (cancelled) return;
                if (data?.available) {
                    setLegacySid(sid);
                    setCounts(data.counts || {});
                } else {
                    // Already claimed or no data — clean up locally so we
                    // don't keep hitting the endpoint.
                    clearLegacySessionId();
                }
            } catch {
                /* swallow */
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [isAuthenticated, dismissed]);

    const totalItems = counts
        ? (counts.assets || 0) + (counts.posts || 0) + (counts.adsum_reviews || 0)
        : 0;
    const vaultBalance = counts?.vault_balance || 0;

    const handleClaim = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/auth/claim-session", {
                session_id: legacySid,
            });
            clearLegacySessionId();
            await refreshAll();
            toast.success("Legacy data merged", {
                description: `${data.assets || 0} assets · ${data.posts || 0} posts · +${data.vault_added || 0} NXT`,
            });
            setDismissed(true);
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                err?.message ||
                "Couldn't merge legacy data";
            toast.error("Claim failed", { description: String(msg) });
        } finally {
            setBusy(false);
        }
    };

    const handleDismiss = () => {
        clearLegacySessionId();
        setDismissed(true);
    };

    if (!legacySid || dismissed) return null;

    return (
        <AnimatePresence>
            <motion.div
                initial={{ opacity: 0, y: -8 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                className="relative z-30 mx-auto mt-4 flex w-full max-w-[960px] flex-wrap items-center justify-between gap-3 rounded-xl border border-[#00F5D4]/30 bg-[#111726] px-5 py-3 text-sm shadow-[0_0_24px_rgba(0,245,212,0.08)]"
                data-testid="legacy-claim-banner"
            >
                <div className="flex items-center gap-3">
                    <Archive className="h-4 w-4 text-[#00F5D4]" />
                    <div className="flex flex-col">
                        <span className="text-[#E8ECF5]">
                            We found{" "}
                            <span className="font-semibold text-[#00F5D4]">
                                {totalItems} items
                            </span>{" "}
                            from a previous visit
                            {vaultBalance > 0 && (
                                <span>
                                    {" "}
                                    + {vaultBalance} NXT
                                </span>
                            )}
                            . Claim them?
                        </span>
                        <span className="font-mono text-[10px] text-[#8A93A6]">
                            {counts?.assets || 0} assets · {counts?.posts || 0}{" "}
                            ready posts · {counts?.adsum_reviews || 0} Adsum
                            reviews
                        </span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        type="button"
                        onClick={handleClaim}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-md bg-gradient-to-r from-[#2F58CD] to-[#2F58CD] px-3 py-1.5 text-xs font-medium text-white transition-all hover:shadow-[0_0_18px_rgba(0,245,212,0.45)] disabled:opacity-50"
                        data-testid="legacy-claim-accept"
                    >
                        <Check className="h-3 w-3" />
                        {busy ? "Claiming…" : "Claim"}
                    </button>
                    <button
                        type="button"
                        onClick={handleDismiss}
                        disabled={busy}
                        className="inline-flex items-center gap-1.5 rounded-md border border-[#1F2A40] px-3 py-1.5 text-xs text-[#8A93A6] transition-colors hover:border-[#8A93A6]/40 hover:text-[#E8ECF5]"
                        data-testid="legacy-claim-dismiss"
                    >
                        <XIcon className="h-3 w-3" />
                        Dismiss
                    </button>
                </div>
            </motion.div>
        </AnimatePresence>
    );
}
